import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { motion, useAnimationControls } from 'motion/react'
import { useStore } from '../../store'

const SIZE = 46

function dockCenter(): { x: number; y: number } | null {
  const dock = document.getElementById('orb-dock')
  if (!dock) return null
  const r = dock.getBoundingClientRect()
  if (r.width === 0) return null
  return { x: r.left + r.width / 2 - SIZE / 2, y: r.top + r.height / 2 - SIZE / 2 }
}

/**
 * The living AI presence. Sits docked at the top of the conversation, breathes
 * while idle, quickens while thinking, pulses while speaking, and physically
 * glides out to point at a chart with a tether of light.
 */
export function Orb() {
  const orb = useStore((s) => s.orb)
  const point = useStore((s) => s.point)
  const clearPoint = useStore((s) => s.clearPoint)
  const sidebarOpen = useStore((s) => s.sidebarOpen)
  const view = useStore((s) => s.view)
  const controls = useAnimationControls()
  const homeRef = useRef({ x: 0, y: 0 })
  const wrapRef = useRef<HTMLDivElement>(null)
  const [placed, setPlaced] = useState(false)
  const [filament, setFilament] = useState<string | null>(null)
  const [gaze, setGaze] = useState({ dx: 0, dy: 0 })
  const [near, setNear] = useState(false)
  const [pop, setPop] = useState(false)

  // Follow the cursor: the orb "looks" toward the pointer and perks up when it's
  // near — alive without stealing clicks from the UI underneath.
  useEffect(() => {
    let raf = 0
    const onMove = (e: MouseEvent) => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const el = wrapRef.current
        if (!el) return
        const r = el.getBoundingClientRect()
        const cx = r.left + r.width / 2
        const cy = r.top + r.height / 2
        const dx = e.clientX - cx
        const dy = e.clientY - cy
        const dist = Math.hypot(dx, dy) || 1
        const reach = Math.min(1, 120 / dist) // lean a touch more as you get close
        setGaze({ dx: (dx / dist) * 3.5 * (0.6 + reach), dy: (dy / dist) * 3.5 * (0.6 + reach) })
        setNear(dist < 90)
      })
    }
    window.addEventListener('mousemove', onMove)
    return () => {
      window.removeEventListener('mousemove', onMove)
      cancelAnimationFrame(raf)
    }
  }, [])

  const poke = () => {
    setPop(true)
    setTimeout(() => setPop(false), 220)
    document.querySelector<HTMLTextAreaElement>('textarea')?.focus()
  }

  // Initial dock placement + keep home updated on resize/layout shifts.
  useLayoutEffect(() => {
    const place = () => {
      const c = dockCenter()
      if (!c) return
      homeRef.current = c
      if (!useStore.getState().point) controls.set({ x: c.x, y: c.y })
      setPlaced(true)
    }
    place()
    const raf = requestAnimationFrame(place)
    const t = setTimeout(place, 120)
    window.addEventListener('resize', place)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(t)
      window.removeEventListener('resize', place)
    }
  }, [controls])

  // Re-dock after the sidebar opens/closes (layout shift the resize event misses).
  useEffect(() => {
    const t = setTimeout(() => {
      const c = dockCenter()
      if (!c) return
      homeRef.current = c
      if (!useStore.getState().point) controls.start({ x: c.x, y: c.y, transition: { duration: 0.3 } })
    }, 300)
    return () => clearTimeout(t)
  }, [sidebarOpen, controls])

  // Pointing behaviour.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    if (point) {
      const el = document.getElementById(point.id)
      if (el) {
        const r = el.getBoundingClientRect()
        const tx = Math.max(10, r.left - SIZE - 14)
        const ty = r.top + 12
        const orbCx = tx + SIZE / 2
        const orbCy = ty + SIZE / 2
        const px = r.left
        const py = r.top + 22
        const midx = (orbCx + px) / 2
        const midy = Math.min(orbCy, py) - 24
        setFilament(`M ${orbCx} ${orbCy} Q ${midx} ${midy} ${px} ${py}`)
        controls.start({
          x: tx,
          y: ty,
          transition: { type: 'spring', stiffness: 210, damping: 24, mass: 0.9 },
        })
        timer = setTimeout(() => clearPoint(), 3600)
      }
    } else {
      setFilament(null)
      const c = homeRef.current
      controls.start({ x: c.x, y: c.y, transition: { type: 'spring', stiffness: 360, damping: 30 } })
    }
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [point, controls, clearPoint])

  if (view !== 'chat') return null

  return (
    <>
      {filament && (
        <svg className="filament" aria-hidden>
          <motion.path
            d={filament}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </svg>
      )}
      <motion.div
        ref={wrapRef}
        className="fixed left-0 top-0 z-50 pointer-events-none"
        animate={controls}
        style={{ width: SIZE, height: SIZE, opacity: placed ? 1 : 0 }}
      >
        <div
          className="orb orb--ignite pointer-events-auto cursor-pointer"
          data-state={orb}
          onClick={poke}
          title="Poke Libera"
          style={{
            width: SIZE,
            height: SIZE,
            transform: `scale(${pop ? 1.22 : near ? 1.08 : 1})`,
            transition: 'transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <div className="orb__aura" style={{ opacity: near ? 1 : undefined }} />
          <div
            className="orb__core"
            style={{ transform: `translate(${gaze.dx}px, ${gaze.dy}px)`, transition: 'transform 0.18s ease-out' }}
          >
            <div className="orb__blob orb__blob--a" />
            <div className="orb__blob orb__blob--b" />
            <div className="orb__spec" style={{ transform: `translate(${gaze.dx * 1.6}px, ${gaze.dy * 1.6}px)` }} />
          </div>
          <div className="orb__comet" />
        </div>
      </motion.div>
    </>
  )
}
